import type {
  CallerLocationInfo,
  LogFunction,
  LogLevel,
  LoggerErrorPayload,
} from '@/composables/useAppLogger/appLogger.types';
import useIdentityStore from '@/store/identity';
import { storeToRefs } from 'pinia';
import useAppErrorLogApi from '@/api/sirta/useAppErrorLog.api';

/**
 * Tente de récupérer les informations basiques sur l'endroit où l'erreur est survenue, à savoir le nom de la
 * composante Vue (si applicable), le nom de la fonction exécutée et le numéro de ligne.
 */
function getCallerFunctionInfos(): CallerLocationInfo | null {
  const e = new Error();

  if (e.stack) {
    try {
      // Chrome / Edge:  caller function located at stack frame index 4, in format like:
      // at <functionName> (<filePath/fileName>:<lineNumber>:<columnNumber>) Firefox:
      //  caller function located at stack frame index 3, in format like:
      // <functionName>@<filePath/fileName>:<lineNumber>:<columnNumber>
      const frames = e.stack.split('\n');

      if (!frames[0]) {
        throw frames;
      }
      const callerFrameIndex = frames[0].startsWith('Error') ? 4 : 3;

      const callerFrameSplit = e.stack.split('\n');

      if (!callerFrameSplit[callerFrameIndex]) {
        throw callerFrameSplit;
      }
      const callerFrame = callerFrameSplit[callerFrameIndex]
        .trim()
        .replace('(', '')
        .replace(')', '');
      let functionName;

      if (callerFrame.indexOf('at ') > -1) {
        functionName = callerFrame
          .substring(callerFrame.indexOf('at ') + 3, callerFrame.lastIndexOf(' '))
          .split('.')
          .reverse()[0];
      } else if (callerFrame.indexOf('@') > -1) {
        functionName = callerFrame.substring(0, callerFrame.indexOf('@'));
      }
      const callerLocationParts = callerFrame.split(':').reverse(); // 0=columnNumber; 1=lineNumber; 2=file
      const fileName = callerLocationParts[2]
        ? callerLocationParts[2].substring(Math.max(callerLocationParts[2].lastIndexOf('/'), callerLocationParts[2].lastIndexOf('\\')) + 1).split('?')[0]
        : '';
      const lineNumber = callerLocationParts[1];

      return { functionName, fileName, lineNumber };
    } catch (err: unknown) {
      window.console.warn('Impossible de déterminer les informations de la fonction appelante:', err);
    }
  }
  return null;
}

function getFormattedPrefix(level: LogLevel, callerInfo: CallerLocationInfo | null): string {
  return `[${level.toUpperCase()}]${callerInfo ? ` [${[callerInfo?.fileName, callerInfo.functionName, callerInfo.lineNumber].filter(Boolean).join(':')}]` : ''}`;
}

export default function useAppLogger() {
  const { fileNumber } = storeToRefs(useIdentityStore());
  const { postError } = useAppErrorLogApi();

  function buildErrorPayload(data: string | Error = '', callerLocationInfos: CallerLocationInfo | null): LoggerErrorPayload {
    const error = typeof data === 'object' ? data : new Error(data);
    const cause: string = typeof error.cause === 'string'
      ? error.cause
      : typeof error.cause !== 'undefined'
        ? JSON.stringify(error.cause || {})
        : '';

    return {
      fileNumber: fileNumber.value,
      exception: {
        name: error.name,
        message: error.message,
        cause,
        stack: error.stack,
        location: callerLocationInfos,
      },
      context: {
        url: window.location.href,
      },
    };
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  function logMessage(level: LogLevel, sendToBackend = false, userData: string | Error = '', ...args: any[]) {
    // Les données sur l'emplacement de l'exception doivent être récupérées ici afin d'avoir des informations justes.
    const callerLocationInfos = getCallerFunctionInfos();
    const payload = buildErrorPayload(userData, callerLocationInfos);

    let message: string = typeof userData === 'string'
      ? userData
      : userData.message ?? '';

    if (payload.exception.cause) {
      message += `\nCause: ${payload.exception.cause}`;
    }

    window.console[level](`${getFormattedPrefix(level, payload.exception?.location)} ${message}\n`, ...args);

    if (sendToBackend) {
      // Envoi silencieux de la requête POST au Backend, sans aucune gestion d'erreur en cas de problème.
      postError(payload).execute();
    }
  }

  /**
   * Messages informatifs, affichés autant en mode production que développement. Ils ne sont jamais envoyés au backend.
   */
  const info: LogFunction = (message, args) => {
    logMessage('info', false, message, args);
  };

  /**
   * Messages affichés uniquement lors du développement local.
   * Ces message ne sont jamais envoyés au backend et n'apparaîssent pas en production.
   *
   */
  const log: LogFunction = (message, args) => {
    if (import.meta.env.DEV) {
      logMessage('log', false, message, args);
    }
  };

  /**
   * Messages d'avertissement affichés lors du développement local seulement.
   * Ils ne sont pas envoyés au backend et n'apparaissent pas en mode production
   */
  const warn: LogFunction = (message, args) => {
    if (import.meta.env.DEV) {
      logMessage('warn', false, message, args);
    }
  };

  /**
   * Messages pour l'investigation durant le développement dans l'environnement local.
   * Ils ne sont pas envoyés au backend et n'apparaissent pas en mode production.
   */
  const debug: LogFunction = (message, args) => {
    if (import.meta.env.DEV) {
      logMessage('debug', false, message, args);
    }
  };

  /**
   * Messages d'erreurs.  Affichés durant le développement local et en mode production.
   * Les informations sont automatiquement envoyés au backend lorsque l'application est en mode "production".
   */
  const error: LogFunction = (error, args) => {
    logMessage('error', import.meta.env.PROD, error, args);
  };

  return {
    info,
    log,
    warn,
    debug,
    error,
  };
}
